import { gpsIcon } from "assets/icons";
import { Button, Select } from "components/form";
import React, { useState } from "react";
import "./TrackOrder.scss";
import { GoCheckCircleFill } from "react-icons/go";
import { getMultiTrackOrderAPI } from "services/user-controllers/order-controller";
import { downloadTrackStatusAPI } from "services/user-controllers/masterController";
import { toast } from "react-toastify";
import moment from "moment";

const TrackOrder = () => {
  //************************* Handle all state *************************//

  const [orderNumber, setOrderNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [trackResults, setTrackResults] = useState([]); // [{ details, list, count }]
  const [notFoundIds, setNotFoundIds] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedItemDetails, setSelectedItemDetails] = useState({});
  const [hasSearched, setHasSearched] = useState(false);
  const [orderIdTabs, setOrderIdTabs] = useState([]);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const selectedResult = trackResults.find((r) => r?.details?.orderId === selectedOrderId) || null;
  const orderDetails = selectedResult?.details || {};
  const orderItemList = selectedResult?.list || [];
  const totalRecordCount = selectedResult?.count || 0;
  const showDocketList = trackResults.length + notFoundIds.length > 1;

  // Best available timestamp for one remark row. A remark stored without its own
  // scanDate/scanTime must NOT fall back to the parent status row's updatedAt —
  // that is just "when the cron last ran", so every such remark would collapse
  // onto one moment and jump above genuinely newer events. Order of preference:
  //   1) the scan's own date/time
  //   2) a "(YYYY-MM-DD | HH:mm:ss)" stamp baked into the remark text
  //   3) when the remark row itself was written
  //   4) the status row time (last resort)
  const remarkTimestamp = (entry, remark) => {
    if (remark?.scanDate) {
      return `${remark.scanDate} ${remark?.scanTime || "00:00:00"}`.trim();
    }
    const m = /\((\d{4}-\d{2}-\d{2})\s*\|\s*(\d{2}:\d{2}:\d{2})\)/.exec(
      remark?.remark || ""
    );
    if (m) return `${m[1]} ${m[2]}`;
    return remark?.createdAt || entry?.updatedAt || entry?.createdAt;
  };

  // flatten the selected item's status history into a newest-first timeline
  const timelineEntries = (selectedItemDetails?.statusArray || [])
    .flatMap((entry) => {
      const rows = entry?.statusRemark?.length > 0 ? entry.statusRemark : [null];
      return rows.map((remark, idx) => ({
        id: `${entry?.id}-${remark?.id ?? idx}`,
        remarkId: remark?.id ?? 0,
        date: remarkTimestamp(entry, remark),
        statusLabel: entry?.statusType?.name || "-----",
        remark: remark?.remark || entry?.ExcelRemarks || "-----",
      }));
    })
    .sort((a, b) => {
      const diff = new Date(b.date) - new Date(a.date);
      // same timestamp (or both unparseable) -> keep insertion order via row id
      return diff !== 0 && !Number.isNaN(diff) ? diff : b.remarkId - a.remarkId;
    });

  //************************* Handle all function *************************//

  // set the default selected sub-item status for a given track result
  const applyDefaultItemSelection = (result) => {
    const firstItem = result?.list?.[0];
    setSelectedItemDetails({
      statusId: firstItem?.Itemstatus?.id,
      statusName: firstItem?.Itemstatus?.name,
      statusArray: firstItem?.orderedItemsStatus,
    });
  };

  // search for one or more order numbers / LSP docket numbers (comma separated)
  const handleSearch = async () => {
    const ids = orderNumber.split(",").map((id) => id.trim()).filter(Boolean);
    if (!ids.length) {
      toast.error("Please enter at least one order number or LSP docket number");
      return;
    }
    setOrderIdTabs(ids);
    setLoading(true);
    try {
      const { data } = await getMultiTrackOrderAPI(ids.join(","));
      if (data?.code === 200) {
        const { found = [], notFound = [] } = data?.data || {};
        setTrackResults(found);
        setNotFoundIds(notFound);
        setHasSearched(true);
        if (found.length > 0) {
          setSelectedOrderId(found[0]?.details?.orderId);
          applyDefaultItemSelection(found[0]);
        } else {
          setSelectedOrderId(null);
          setSelectedItemDetails({});
          toast.error("No matching orders found");
        }
      } else {
        setTrackResults([]);
        setNotFoundIds([]);
        setSelectedOrderId(null);
        setHasSearched(true);
        toast.error(data?.message);
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong while tracking your order(s)");
    } finally {
      setLoading(false);
    }
  };

  // switch the detail panel to a different searched docket/order
  const handleSelectOrder = (orderId) => {
    if (orderId === selectedOrderId) return;
    setSelectedOrderId(orderId);
    const result = trackResults.find((r) => r?.details?.orderId === orderId);
    applyDefaultItemSelection(result);
  };

  // check if delay reason should show (ETA passed and a reason is available,
  // regardless of whether the order has since been delivered)
  const isOrderDelayed = () => {
    if (!orderDetails?.ETA) return false;
    const isPastETA = moment().isAfter(moment(orderDetails?.ETA).endOf("day"));
    return isPastETA && !!orderDetails?.userDelayReason;
  };

  // download Excel status for all searched order IDs
  const downloadStatusHandler = async () => {
    if (orderIdTabs.length === 0) return;
    setDownloadLoading(true);
    try {
      const response = await downloadTrackStatusAPI(orderIdTabs.join(","));
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `order_status_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error("Failed to download status");
    } finally {
      setDownloadLoading(false);
    }
  };

  // open the POD for the currently selected order in a new tab
  const viewPODHandler = () => {
    if (orderDetails?.PODUrl) {
      window.open(orderDetails.PODUrl, "_blank");
    } else {
      toast.error("POD not available for this order yet");
    }
  };

  // onChange Handler for item select
  const orderItemOnChangeHandler = (e) => {
    const { value } = e.target;
    const findOrderItemObj = orderItemList.find((el) => el?.Itemid === value);
    if (findOrderItemObj?.Itemid?.length > 0) {
      let orderItemStatusData = {
        statusId: findOrderItemObj?.Itemstatus?.id,
        statusName: findOrderItemObj?.Itemstatus?.name,
        statusArray: findOrderItemObj?.orderedItemsStatus,
      };
      setSelectedItemDetails(orderItemStatusData);
    }
  };

  //************************* Handle HTML CODE *************************//
  return (
    <>
      <div className="track_order">
        <div class="dashboard_title">
          <h4>Track Your Order</h4>
        </div>

        <div className="track_order_wrap">
          <div className="search_wrap">
            <img src={gpsIcon} alt="" />
            <input
              type="search"
              value={orderNumber}
              className="input form-control"
              placeholder="Enter Order Number or LSP Docket No (comma separated for multiple)"
              onChange={(e) => setOrderNumber(e?.target?.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <Button onClick={handleSearch} disabled={loading}>
              {loading ? "Searching..." : "Search Now"}
            </Button>
            {trackResults.length > 0 && (
              <button
                type="button"
                className="download_btn"
                disabled={downloadLoading}
                onClick={downloadStatusHandler}
              >
                {downloadLoading ? "Downloading..." : "Download Excel"}
              </button>
            )}
          </div>
        </div>

        {/*-------------------- Order Details and Status Details ------------------*/}
        {trackResults.length > 0 || notFoundIds.length > 0 ? (
          <div className={`track_multi_wrap${showDocketList ? " has_list" : ""}`}>
            {showDocketList && (
              <div className="docket_list">
                {trackResults.map((r) => (
                  <button
                    key={r?.details?.orderId}
                    type="button"
                    className={`docket_chip${selectedOrderId === r?.details?.orderId ? " active" : ""}`}
                    onClick={() => handleSelectOrder(r?.details?.orderId)}
                  >
                    {r?.details?.orderId}
                  </button>
                ))}
                {notFoundIds.map((id) => (
                  <span key={id} className="docket_chip not_found" title="No matching order found">
                    {id}
                  </span>
                ))}
              </div>
            )}

            <div className="docket_detail">
              {selectedResult ? (
                <div className="docket_detail_top">
                  <div className="docket_info_grid">
                    <ul>
                      <li>
                        <span>Order ID</span>
                        <b>{orderDetails?.orderId || "------"}</b>
                      </li>
                      <li>
                        <span>Docket No</span>
                        <b>{orderDetails?.LSPDocketNo || "------"}</b>
                      </li>
                      <li>
                        <span>Booking Date</span>
                        <b>{moment(orderDetails?.createdAt).format("MMM DD, YYYY hh:mm A") || "------"}</b>
                      </li>
                      <li>
                        <span>Origin</span>
                        <b>{orderDetails?.Frompincode || "------"}{orderDetails?.fromState ? ` (${orderDetails.fromState})` : ""}</b>
                      </li>
                      <li>
                        <span>Destination</span>
                        <b>{orderDetails?.ToPincode || "------"}{orderDetails?.toState ? ` (${orderDetails.toState})` : ""}</b>
                      </li>
                      <li>
                        <span>Estimated Delivery Date</span>
                        <b>{moment(orderDetails?.ETA).format("MMM DD, YYYY") || "------"}</b>
                      </li>
                      <li>
                        <span>Status</span>
                        <b>{orderDetails?.status || "-----"}</b>
                      </li>
                      <li>
                        <span>POD</span>
                        {orderDetails?.PODUrl ? (
                          <button type="button" className="link_btn" onClick={viewPODHandler}>
                            View / Download POD
                          </button>
                        ) : (
                          <b>Not available</b>
                        )}
                      </li>
                      <li>
                        <span>Consignor Name</span>
                        <b>{orderDetails?.accountName || "------"}</b>
                      </li>
                      <li>
                        <span>Consignee Name</span>
                        <b>{orderDetails?.deliverypersonname || "------"}</b>
                      </li>
                      <li>
                        <span>Number of Package</span>
                        <b>{totalRecordCount || 0}</b>
                      </li>
                      <li>
                        <span>Latest Remark</span>
                        <b>{orderDetails?.latestRemark?.ExcelRemarks || "------"}</b>
                      </li>
                      {isOrderDelayed() && (
                        <li>
                          <span>Delay Reason</span>
                          <b>{orderDetails?.userDelayReason || "------"}</b>
                        </li>
                      )}
                    </ul>
                  </div>

                  <div className="status_timeline_wrap">
                    <div className="has_child">
                      <h4>Track Order</h4>
                      <Select onChange={orderItemOnChangeHandler}>
                        {orderItemList?.length > 0 &&
                          orderItemList?.map((el) => (
                            <option key={el?.id} value={el?.Itemid}>
                              {el?.Itemid}
                            </option>
                          ))}
                      </Select>
                    </div>

                    <ul className="status_timeline">
                      {timelineEntries.length > 0 ? (
                        timelineEntries.map((entry) => (
                          <li key={entry.id}>
                            <span className="icon">
                              <GoCheckCircleFill />
                            </span>
                            <div className="content">
                              <span className="timestamp">
                                {moment(entry.date).isValid()
                                  ? moment(entry.date).format("YYYY-MM-DD HH:mm:ss")
                                  : "------"}
                              </span>
                              <p>
                                <b>{entry.statusLabel}</b> - {entry.remark}
                              </p>
                            </div>
                          </li>
                        ))
                      ) : (
                        <li className="empty">No tracking history yet</li>
                      )}
                    </ul>
                  </div>
                </div>
              ) : (
                <h4>Select a docket number to view details</h4>
              )}
            </div>
          </div>
        ) : (
          <h4>{hasSearched ? "No matching orders found" : "Search with order number"}</h4>
        )}
        {/*-------------------- Close Order Details and Status Details ------------------*/}
      </div>
    </>
  );
};

export default TrackOrder;
